// frontend/client/src/hooks/useTheme.js
// DashboardLayout imports this as: import { useTheme } from '../hooks/useTheme'
// This file must exist at src/hooks/useTheme.js

import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getContrastText } from '../utils/colorContrast';

const CSS_VARS = {
  light: {
    '--bg-canvas':        '#F9FAFB',
    '--bg-surface':       '#FFFFFF',
    '--bg-surface-hover': '#F5F3FF',
    '--bg-sidebar':       '#111827',
    '--text-main':        '#111827',
    '--text-muted':       '#6B7280',
    '--text-light':       '#9CA3AF',
    '--text-inverse':     '#FFFFFF',
    '--border-light':     '#E5E7EB',
    '--border-medium':    '#D1D5DB',
    '--shadow-sm':        '0 2px 4px rgba(0,0,0,0.02)',
    '--shadow-md':        '0 4px 12px rgba(0,0,0,0.04)',
    '--shadow-lg':        '0 8px 24px rgba(0,0,0,0.04)',
    '--shadow-xl':        '0 12px 32px rgba(0,0,0,0.06)',
  },
  dark: {
    '--bg-canvas':        '#0F0F13',
    '--bg-surface':       '#1A1A24',
    '--bg-surface-hover': '#22223A',
    '--bg-sidebar':       '#0A0A0F',
    '--text-main':        '#F3F4F6',
    '--text-muted':       '#9CA3AF',
    '--text-light':       '#6B7280',
    '--text-inverse':     '#111827',
    '--border-light':     '#2D2D3D',
    '--border-medium':    '#3D3D50',
    '--shadow-sm':        '0 2px 4px rgba(0,0,0,0.2)',
    '--shadow-md':        '0 4px 12px rgba(0,0,0,0.3)',
    '--shadow-lg':        '0 8px 24px rgba(0,0,0,0.4)',
    '--shadow-xl':        '0 12px 32px rgba(0,0,0,0.5)',
  }
};

const BRAND_VARS = {
  '--brand-primary':       '#4F46E5',
  '--brand-primary-hover': '#4338CA',
  '--brand-secondary':     '#EEF2FF',
  '--brand-border':        '#C7D2FE',
  '--brand-accent':        '#8B5CF6',
  '--shadow-brand':        '0 4px 12px rgba(79, 70, 229, 0.25)',
  '--font-body':           "'Plus Jakarta Sans', sans-serif",
  '--font-display':        "'Syne', sans-serif",
};

function applyTheme(resolved, brandColors = null) {
  const root = document.documentElement;
  const vars = CSS_VARS[resolved] || CSS_VARS.light;

  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  Object.entries(BRAND_VARS).forEach(([k, v]) => root.style.setProperty(k, v));

  // Apply brand color overrides from Brand Scan if available.
  //
  // brandColors comes from an automated Instagram photo scan — there's no
  // human curation step, so it can land on anything (neon green, pastel
  // pink, near-black). Every button in the app that pairs
  // var(--brand-primary)/var(--brand-secondary) with a hardcoded
  // color: white (or a hardcoded dark color) assumed these variables would
  // always be a safe mid-tone indigo/purple. That assumption breaks the
  // moment a real scan produces something extreme, and since this is a
  // single global variable, it breaks every consumer at once (Settings,
  // Content Lab, Video Tools, Dashboard) rather than one isolated button.
  //
  // Computing and exposing a contrast-safe companion variable here — at
  // the one place these colors enter the app — means any component can
  // opt into var(--brand-primary-contrast) instead of a hardcoded color
  // and be safe against ANY value brandColors ends up holding.
  if (brandColors) {
    if (brandColors[0]) {
      root.style.setProperty('--brand-primary', brandColors[0]);
      root.style.setProperty('--brand-primary-contrast', getContrastText(brandColors[0]));
    }
    if (brandColors[1]) {
      root.style.setProperty('--brand-secondary', brandColors[1]);
      root.style.setProperty('--brand-secondary-contrast', getContrastText(brandColors[1]));
    }
    if (brandColors[2]) {
      root.style.setProperty('--brand-accent', brandColors[2]);
      root.style.setProperty('--brand-accent-contrast', getContrastText(brandColors[2]));
    }
  }

  // Inline styles (set above) always beat stylesheet selectors, so the CSS
  // variables are already correct regardless of this class. But index.css
  // has rules scoped directly to `.dark` (e.g. .dark input::placeholder,
  // .dark .force-readable) that only exist as class selectors, not
  // variables — those need the class present on the DOM to apply at all.
  root.classList.toggle('dark', resolved === 'dark');

  root.setAttribute('data-theme', resolved);
  document.body.style.backgroundColor = vars['--bg-canvas'];
  document.body.style.color           = vars['--text-main'];
}

function resolve(preference) {
  if (preference === 'dark')  return 'dark';
  if (preference === 'light') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * useTheme — call once in DashboardLayout.
 * Reads userData.settings.theme and userData.brandColors,
 * applies CSS variables to :root, and listens for OS theme changes.
 */
export function useTheme() {
  const { userData } = useAuth() || {};
  const preference  = userData?.settings?.theme || 'system';
  const brandColors = userData?.brandColors     || null;

  useEffect(() => {
    const resolved = resolve(preference);
    applyTheme(resolved, brandColors);

    if (preference === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => applyTheme(e.matches ? 'dark' : 'light', brandColors);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [preference, brandColors]);
}

// Named export matches DashboardLayout import:
// import { useTheme } from '../hooks/useTheme';
export default useTheme;